import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, Share2, CheckCircle2, ClipboardCopy, Palette } from "lucide-react";
import { toast } from "sonner";
import { Button, Card, Badge } from "../ui";
import { eventInviteUrl, eventPublicPath } from "../../lib/eventLinks";
import { buildEventWhatsAppSummary, copyWhatsAppSummary, openWhatsAppShare } from "../../lib/share";
import {
  completeHostOnboarding,
  HOST_ONBOARDING_RETURN_PATH,
  markHostOnboardingActivated,
} from "../../lib/hostOnboarding";
import { useAuth } from "../../lib/auth";

export default function CreateEventSuccessScreen({ event, isEdit, onboardingReturn = false }) {
  const nav = useNavigate();
  const { updateAccount } = useAuth();
  const link = eventInviteUrl(event);
  const dashLink = `/dashboard/${event.id}`;
  const shareText = buildEventWhatsAppSummary(event, { link });
  const onboardingPath = HOST_ONBOARDING_RETURN_PATH;
  const themePath = "/onboarding?step=theme";

  useEffect(() => {
    if (!onboardingReturn || isEdit) return;
    markHostOnboardingActivated({ step: "loop" });
    // Publishing an event is the aha moment — mark setup complete so the feed
    // doesn't nag. Theme remains reachable via ?step=theme.
    let cancelled = false;
    (async () => {
      try {
        await completeHostOnboarding(updateAccount);
      } catch {
        /* resume banner remains if this fails */
      }
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [onboardingReturn, isEdit, updateAccount]);

  const subtitle = isEdit
    ? "Your invite link is the same — no need to re-share unless you want to."
    : onboardingReturn
      ? "This is the moment — share the link and watch the guest list grow."
      : "Share the link on WhatsApp — the hype starts now.";

  function handleDoneForNow() {
    nav("/events", { replace: true });
  }

  return (
    <div className="min-h-screen bg-canvas font-body text-ink">
      <div className="max-w-page mx-auto px-4 py-8">
        <Link
          to={
            onboardingReturn
              ? onboardingPath
              : isEdit
                ? (eventPublicPath(event) || "/events")
                : "/events"
          }
          className="inline-flex items-center gap-1.5 font-ui font-medium text-[13px] text-ink/55 hover:text-ink transition-colors min-h-[44px]"
          data-testid="back-home-link"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2} />{" "}
          {onboardingReturn ? "Back to setup" : isEdit ? "Back to event" : "My events"}
        </Link>

        <Card hero color="night" className="p-7 mt-6 relative overflow-hidden">
          <div className="absolute inset-0 home-hero-wash pointer-events-none opacity-80" aria-hidden />
          <div className="relative">
            <Badge color="white" className="bg-white/15 text-cream border-white/25">
              <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} /> {isEdit ? "Changes saved" : "Event is live"}
            </Badge>
            <h1 className="font-display font-semibold text-[32px] sm:text-[40px] leading-[1.05] tracking-[-0.02em] mt-3 text-cream text-balance">
              {event.name}
            </h1>
            <p className="mt-3 font-ui font-normal text-[15px] text-cream/80 text-pretty">{subtitle}</p>
          </div>
        </Card>

        <Card className="p-5 mt-5">
          <p className="font-ui font-medium text-[12px] text-ink/50 uppercase tracking-wide mb-2">Your invite link</p>
          <div className="flex flex-col sm:flex-row items-stretch gap-2">
            <input className="field font-mono text-[13px]" value={link} readOnly data-testid="invite-link-input" />
            <Button
              variant="secondary"
              onClick={() => {
                navigator.clipboard.writeText(link);
                toast.success("Link copied!");
              }}
              data-testid="copy-link-btn"
            >
              <Copy className="w-4 h-4" strokeWidth={2} /> Copy
            </Button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="primary" onClick={() => openWhatsAppShare(shareText)} data-testid="share-btn">
              <Share2 className="w-4 h-4" strokeWidth={2} /> Share on WhatsApp
            </Button>
            <Button
              variant="secondary"
              onClick={async () => {
                await copyWhatsAppSummary(shareText);
                toast.success("Message copied — paste into WhatsApp");
              }}
              data-testid="copy-hype-blurb-btn"
            >
              <ClipboardCopy className="w-4 h-4" strokeWidth={2} /> Copy message
            </Button>
            <Button variant="secondary" onClick={() => nav(eventPublicPath(event) || "/events")} data-testid="view-event-btn">
              View RSVP page →
            </Button>
          </div>
        </Card>

        {onboardingReturn ? (
          <Card className="p-5 mt-5" data-testid="onboarding-continue-card">
            <p className="font-ui font-medium text-[12px] text-ink/50 uppercase tracking-wide mb-2">Next</p>
            <p className="text-[14px] font-normal text-ink/65 mb-3 text-pretty">
              You’re live. Share above, then optionally pick a look — or stop here.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                onClick={() => nav(themePath)}
                data-testid="continue-onboarding-theme-btn"
              >
                <Palette className="w-4 h-4" strokeWidth={2} /> Pick a look
              </Button>
              <Button
                variant="primary"
                onClick={handleDoneForNow}
                data-testid="onboarding-done-btn"
              >
                I’m done — go to events →
              </Button>
            </div>
            <Button
              variant="link"
              size="sm"
              className="mt-3"
              onClick={() => nav(onboardingPath)}
              data-testid="continue-onboarding-btn"
            >
              Add a school or finish setup →
            </Button>
          </Card>
        ) : null}

        <Card className="p-5 mt-5">
          <p className="font-ui font-medium text-[12px] text-ink/50 uppercase tracking-wide mb-2">Host dashboard</p>
          <p className="text-[14px] font-normal text-ink/65 mb-3 text-pretty">
            Live counts and guest tools — linked to your account on any device.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="night" onClick={() => nav(dashLink)} data-testid="open-dashboard-btn">
              Open dashboard →
            </Button>
            <Button
              variant="secondary"
              onClick={() => nav(onboardingReturn ? onboardingPath : "/events")}
            >
              {onboardingReturn ? "Back to setup" : "All my events"}
            </Button>
          </div>
          {!isEdit && (
            <p className="mt-4 text-[13px] font-normal text-ink/65 leading-relaxed text-pretty" data-testid="co-host-success-hint">
              Sharing the door with someone? After they RSVP,{" "}
              <Link
                to={`/e/${event.id}/edit`}
                className="underline underline-offset-2 font-medium text-ink"
              >
                edit the event
              </Link>{" "}
              to give them guest-list access.
            </p>
          )}
        </Card>
      </div>
    </div>
  );
}
